import { useState } from 'react';
import { createStyles, Stack } from '@mantine/core';

import { Title } from '~/domains/design-system/Title';
import type { Contest } from '~/domains/contest/domains/services/interfaces/contest.interface';
import { Drawer } from '~/domains/design-system/components/Drawer/Drawer';
import dt from '~/domains/contest/domains/manage/domains/common/testing.const';

import type { BaseField, FieldDescriptor, StaticFieldProps } from '../../types/common';
import { Field } from '../common/SelectFieldOptions';
import type { SingleSelectField } from '../SingleSelectField/types';
import type { FieldQuery } from '../../../context/types/store';

export type PreviousSelectFieldType = BaseField & {
  type: 'PreviousSelectField';
  option: SingleSelectField['options'][0];
  contests: Contest[];
  fields: any[];
};

function SelectModalTitle({ label }: { label?: string }) {
  return label ? (
    <Title ta="center" variant="headline-large">
      {label}
    </Title>
  ) : null;
}

const useStyles = createStyles((theme) => ({
  wrapper: {
    padding: theme.other.spacing._16,
    background: theme.colors.gray[0],
    height: '100%',
    [theme.fn.smallerThan('md')]: {
      minHeight: 'calc(100vh - 120px)',
    },
  },
  drawer: {
    height: '100%',
  },
  body: {
    marginLeft: /* eslint-disable-line local-rules/use-theme-tokens */ -20,
    marginRight: /* eslint-disable-line local-rules/use-theme-tokens */ -20,
    marginBottom: /* eslint-disable-line local-rules/use-theme-tokens */ -20,
    height: 'calc(100% - 100px)',
  },
  leagueHeader: {
    position: 'sticky',
    top: -16,
    background: theme.white,
    zIndex: 2,
  },
}));

// This component uses hard coded keys as this comp
// should be used only in one place
function PreviousSelectFieldElement({
  field,
  sectionKey,
  path,
  FieldSwitch,
  onValueChange,
}: StaticFieldProps<PreviousSelectFieldType>) {
  const { classes } = useStyles();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Field
        onChange={() => setIsOpen(true)}
        option={field.option}
        field={field as unknown as SingleSelectField}
        isChecked={isOpen}
      />

      <Drawer
        title={<SelectModalTitle label={field?.label} />}
        onClose={() => {
          setIsOpen(false);
          onValueChange(false);
        }}
        onToggle={setIsOpen}
        open={isOpen}
        data-test-id={dt.previous.container}
        contentProps={{ className: classes.body }}
      >
        <div className={classes.leagueHeader}>
          {field.fields
            .filter((key) => key === 'contestLeagueId')
            .map((key) => (
              <FieldSwitch key={key} fieldKey={key} sectionKey={sectionKey} path={path} />
            ))}
        </div>

        <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={8} className={classes.wrapper}>
          {field.fields
            .filter((key) => key === 'contestType')
            .map((key) => (
              <FieldSwitch key={key} fieldKey={key} sectionKey={sectionKey} path={path} />
            ))}
          <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={10}>
            {field.fields
              .filter((key) => key === 'contestId')
              .map((key) => (
                <FieldSwitch key={key} fieldKey={key} sectionKey={sectionKey} path={path} />
              ))}
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
}

const Descriptor: FieldDescriptor<PreviousSelectFieldType> = {
  FieldComponent: PreviousSelectFieldElement,
  name: 'PreviousSelectField',
  parseFieldResponse: ({ field, fieldKey, parse }) => {
    const fields = field.fields.map((childField) => parse(childField)).filter(Boolean);

    const normalizedField: FieldQuery = {
      key: fieldKey,
      type: field.type,
      data: {
        ...field,
        fields: fields.map((f) => f[0].key),
      },
    };

    return [normalizedField, fields];
  },
};

export default Descriptor;
